// The log-in page's stage (owner design, 2026-10-25; D-063): root says hi, then walks to one side,
// grabs a feature board by its handle and pulls it in, presents it, and does the same from the other
// side for the next one: Ecommerce, Import Products, Digital Marketing, Sampark, Daraz, then waves
// goodbye and starts again. root is the real 3D model (LoginStageScene, loaded after the page is
// idle); the boards are page elements that follow root's fist every frame. Reduced motion: root
// stands still and the boards simply change. Screen readers get the five features as a list.
import { i18n, t } from "@ady/i18n";
import { BrandIcon, cx, Icon, type BrandName, type IconName } from "@ady/ui";
import { Suspense, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { LazyLoginStageScene, type StageDrive } from "../ai-mascot";
import { useMediaQuery } from "../ai-mascot/canvasSupport";
import { LOGIN, WALK_SPEED } from "../ai-mascot/mascot.config";
import type { MascotState } from "../ai-mascot/mascot.types";

type BoardKey = "ecommerce" | "import" | "marketing" | "sampark" | "daraz";
/** A feature tile at the foot of a board: our icon, or the service's own mark. */
type Tile = { key: string; icon?: IconName; brand?: BrandName };
interface BoardDef {
  key: BoardKey;
  icon: IconName;
  tiles: Tile[];
}
const BOARDS: BoardDef[] = [
  {
    key: "ecommerce",
    icon: "store",
    tiles: [
      { key: "store", icon: "store" },
      { key: "orders", icon: "orders" },
      { key: "stock", icon: "box" },
      { key: "couriers", icon: "truck" },
    ],
  },
  {
    key: "import",
    icon: "ship",
    tiles: [
      { key: "suppliers", icon: "globe" },
      { key: "quote", icon: "tag" },
      { key: "shipping", icon: "ship" },
      { key: "tracking", icon: "mapPin" },
    ],
  },
  {
    key: "marketing",
    icon: "megaphone",
    tiles: [
      { key: "meta", brand: "meta" },
      { key: "google", brand: "google" },
      { key: "sms", icon: "phone" },
      { key: "coupons", icon: "percent" },
      { key: "reports", icon: "chart" },
    ],
  },
  {
    key: "sampark",
    icon: "chat",
    tiles: [
      { key: "website", icon: "globe" },
      { key: "messenger", brand: "messenger" },
      { key: "instagram", brand: "instagram" },
      { key: "whatsapp", brand: "whatsapp" },
      { key: "tiktok", brand: "tiktok" },
    ],
  },
  {
    key: "daraz",
    icon: "bag",
    tiles: [
      { key: "daraz", brand: "darazmarket" },
      { key: "orders", icon: "orders" },
      { key: "labels", icon: "printer" },
      { key: "sync", icon: "refresh" },
      { key: "payouts", icon: "wallet" },
    ],
  },
];
/** Board content is laid out at this width, then scaled to the stage; its height is its content's. */
const BOARD_W = 324;

type Kind = "intro" | "walk" | "grab" | "pull" | "hold" | "outro" | "bye";
interface Phase {
  kind: Kind;
  /** Board index, -1 for intro / outro / bye. */
  i: number;
  ms: number;
  /** root's x (px) at the start and end of the phase. */
  from: number;
  to: number;
}

interface Geometry {
  W: number;
  H: number;
  floorY: number;
  rootPx: number;
  grip: number;
  gripY: number;
  bw: number;
  k: number;
  /** Boards stand on the floor: their bottom edge, px from the stage's bottom. */
  boardBottom: number;
  center: number;
  restL: number;
  restR: number;
}

function geometry(W: number, H: number): Geometry {
  const floorY = Math.round(H * 0.8);
  const rootPx = Math.round(H * (W < 520 ? 0.34 : 0.4));
  const m = Math.round(W * 0.045);
  const grip = LOGIN.grip.x * rootPx;
  const restL = m + 0.36 * rootPx;
  const boardLeftR = restL + grip + 4;
  const bw = Math.round(Math.max(160, Math.min(345, W - boardLeftR - m)));
  const k = bw / BOARD_W;
  return {
    W,
    H,
    floorY,
    rootPx,
    grip,
    gripY: floorY - LOGIN.grip.y * rootPx,
    bw,
    k,
    boardBottom: H - floorY + 2,
    center: W / 2,
    restL,
    restR: W - restL,
  };
}

const sideOf = (i: number): 1 | -1 => (i % 2 === 0 ? 1 : -1); // 1: the board comes from the right

function phasesFor(g: Geometry, reduced: boolean): Phase[] {
  if (reduced) {
    const list: Phase[] = [{ kind: "intro", i: -1, ms: 3400, from: g.restL, to: g.restL }];
    BOARDS.forEach((_, i) => list.push({ kind: "hold", i, ms: 5200, from: g.restL, to: g.restL }));
    return list;
  }
  const walkPx = WALK_SPEED * LOGIN.walkTimeScale * g.rootPx; // px/s with the feet planted
  const pullPx = LOGIN.pullSpeed * g.rootPx;
  const ms = (d: number, v: number) => Math.max(450, (Math.abs(d) / v) * 1000);
  const list: Phase[] = [{ kind: "intro", i: -1, ms: 3400, from: g.center, to: g.center }];
  let x = g.center;
  BOARDS.forEach((_, i) => {
    const s = sideOf(i);
    const edge = s === 1 ? g.W - g.grip - 16 : g.grip + 16;
    const rest = s === 1 ? g.restL : g.restR;
    list.push({ kind: "walk", i, ms: ms(edge - x, walkPx), from: x, to: edge });
    list.push({ kind: "grab", i, ms: 650, from: edge, to: edge });
    list.push({ kind: "pull", i, ms: ms(rest - edge, pullPx), from: edge, to: rest });
    list.push({ kind: "hold", i, ms: 4200, from: rest, to: rest });
    x = rest;
  });
  list.push({ kind: "outro", i: -1, ms: ms(g.center - x, walkPx), from: x, to: g.center });
  list.push({ kind: "bye", i: -1, ms: 3000, from: g.center, to: g.center });
  return list;
}

const easeOut = (u: number) => 1 - (1 - u) ** 3;
const easeIn = (u: number) => u ** 3;

/**
 * One board's left edge (px), whether it is on stage, and whether root holds its handle (the handle
 * shows only then: once root lets go it fades, so it never hangs beside root), at phase p, progress u.
 */
function boardAt(g: Geometry, phases: Phase[], j: number, p: number, u: number, rootX: number) {
  const s = sideOf(j);
  const off = s === 1 ? g.W + 12 : -g.bw - 12;
  const held = (x: number) => (s === 1 ? x + g.grip + 4 : x - g.grip - 4 - g.bw);
  const grabAt = phases.findIndex((ph) => ph.kind === "grab" && ph.i === j);
  const pullAt = grabAt + 1;
  const next = phases.findIndex((ph) => ph.kind === "pull" && ph.i === j + 1);
  const exitAt = next >= 0 ? next : phases.findIndex((ph) => ph.kind === "outro");
  const settled = held(phases[pullAt]!.to);
  if (p < grabAt || p > exitAt) return { x: off, shown: false, gripped: false };
  if (p === grabAt) {
    // The board peeks in as root grabs its handle.
    const peek = held(phases[grabAt]!.to);
    return { x: off + (peek - off) * easeOut(u), shown: true, gripped: true };
  }
  if (p === pullAt) return { x: held(rootX), shown: true, gripped: true };
  if (p === exitAt) {
    // Out of the way of the next board (or of root walking home), quicker than root moves.
    const v = Math.min(1, (u * phases[exitAt]!.ms) / Math.min(1500, phases[exitAt]!.ms * 0.55));
    return { x: settled + (off - settled) * easeIn(v), shown: v < 1, gripped: false };
  }
  return { x: settled, shown: true, gripped: false };
}

function rootPose(ph: Phase): {
  state: MascotState;
  speed: number;
  heading: number;
  mirror: boolean;
} {
  const s = ph.i >= 0 ? sideOf(ph.i) : 1;
  switch (ph.kind) {
    case "walk":
    case "outro":
      return {
        state: "walking",
        speed: LOGIN.walkTimeScale,
        heading: Math.sign(ph.to - ph.from) * LOGIN.walkHeading,
        mirror: false,
      };
    // A board on the right is held in root's left hand (Pulling), one on the left in its right hand
    // (PullingRight): two clips, never a flipped model, so letting go never pops the pose.
    case "grab":
      return {
        state: s === 1 ? "pulling" : "pullingRight",
        speed: 0.8,
        heading: s * 0.2,
        mirror: false,
      };
    case "pull":
      return {
        state: s === 1 ? "pulling" : "pullingRight",
        speed: LOGIN.pullTimeScale,
        heading: s * 0.2,
        mirror: false,
      };
    case "hold":
      return { state: "talking", speed: 1, heading: s * LOGIN.presentHeading, mirror: false };
    default:
      return { state: "waving", speed: 1, heading: 0, mirror: false };
  }
}

/**
 * When the loop first started (performance.now()): the log-in and code screens both show the stage,
 * so moving from one to the other carries on where root was instead of saying hi again.
 */
let loopStart: number | null = null;

/** The phase and its start time for "now", continuing the shared loop. */
function resume(phases: Phase[], now: number): { pi: number; t0: number } {
  if (loopStart === null) {
    loopStart = now;
    return { pi: 0, t0: now };
  }
  const total = phases.reduce((sum, ph) => sum + ph.ms, 0);
  let left = (now - loopStart) % total;
  let pi = 0;
  while (left >= phases[pi]!.ms) {
    left -= phases[pi]!.ms;
    pi += 1;
  }
  return { pi, t0: now - left };
}

export function LoginShowcase() {
  const box = useRef<HTMLDivElement>(null);
  const [size, setSize] = useState({ w: 0, h: 0 });
  useLayoutEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setSize({ w: el.clientWidth, h: el.clientHeight });
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  const reduced = useMediaQuery("(prefers-reduced-motion: reduce)");
  const g = useMemo(() => (size.w ? geometry(size.w, size.h) : null), [size.w, size.h]);
  const phases = useMemo(() => (g ? phasesFor(g, reduced) : []), [g, reduced]);

  // three.js and the model load once the page is idle, so the log-in form comes first. The idle wait
  // is capped: the busy sign-in page kept the browser from ever being "idle", and root started
  // loading 3.3 s after the page had loaded (measured 2026-11-03, comment c3).
  const [load, setLoad] = useState(false);
  const [ready, setReady] = useState(false);
  useEffect(() => {
    const go = () => setLoad(true);
    const w = window as {
      requestIdleCallback?: (cb: () => void, opts?: { timeout: number }) => number;
      cancelIdleCallback?: (id: number) => void;
    };
    const ric = w.requestIdleCallback;
    const id = ric ? ric(go, { timeout: 200 }) : window.setTimeout(go, 200);
    // No WebGL or a slow network: the boards start on their own.
    const fallback = window.setTimeout(() => setReady(true), 6000);
    return () => {
      if (ric) w.cancelIdleCallback?.(id);
      else window.clearTimeout(id);
      window.clearTimeout(fallback);
    };
  }, []);

  const drive = useRef<StageDrive>({ x: 0, heading: 0, mirror: false });
  const boardEls = useRef<(HTMLDivElement | null)[]>([]);
  const handleEls = useRef<(HTMLSpanElement | null)[]>([]);
  const [p, setP] = useState(0);
  const ph = phases[p] ?? phases[0];

  useEffect(() => {
    if (!g || !phases.length) return;
    drive.current = { x: phases[0]!.from, heading: 0, mirror: false };
    if (!ready) return;
    let { pi, t0 } = resume(phases, performance.now());
    let frame = 0;
    setP(pi);
    const tick = (now: number) => {
      let cur = phases[pi]!;
      let u = (now - t0) / cur.ms;
      while (u >= 1) {
        t0 += cur.ms;
        pi = (pi + 1) % phases.length;
        cur = phases[pi]!;
        u = (now - t0) / cur.ms;
        setP(pi);
      }
      const x = cur.from + (cur.to - cur.from) * u;
      const pose = rootPose(cur);
      drive.current = { x, heading: pose.heading, mirror: pose.mirror };
      BOARDS.forEach((_, j) => {
        const el = boardEls.current[j];
        if (!el) return;
        if (reduced) {
          const on = cur.kind === "hold" && cur.i === j;
          el.style.transform = `translateX(${Math.round(g.restL + g.grip + 4)}px)`;
          el.style.opacity = on ? "1" : "0";
          el.style.visibility = on ? "visible" : "hidden";
          return;
        }
        const b = boardAt(g, phases, j, pi, u, x);
        el.style.transform = `translateX(${b.x.toFixed(1)}px)`;
        el.style.opacity = "1";
        el.style.visibility = b.shown ? "visible" : "hidden";
        const handle = handleEls.current[j];
        if (handle) {
          const edge = sideOf(j) === 1 ? -10 : g.bw - 6;
          handle.style.transform = `translateX(${(b.x + edge).toFixed(1)}px)`;
          handle.style.opacity = b.gripped ? "1" : "0";
        }
      });
      frame = requestAnimationFrame(tick);
    };
    frame = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame);
  }, [g, phases, ready, reduced]);

  const pose = ph ? rootPose(ph) : null;
  const active = ph && ph.i >= 0 && ready ? ph.i : -1;
  const bubble = ready && ph && (ph.kind === "intro" || ph.kind === "bye") ? ph : null;

  return (
    <div ref={box} className="absolute inset-0 overflow-hidden">
      {/* For screen readers: the five parts of Adyvance, without the animation. */}
      <ul className="sr-only">
        {BOARDS.map((b) => (
          <li key={b.key}>
            {t(`aiMascot.showcase.boards.${b.key}.title`)}:{" "}
            {t(`aiMascot.showcase.boards.${b.key}.lead`)}
          </li>
        ))}
      </ul>
      {g ? (
        <div aria-hidden="true" className="absolute inset-0">
          <span
            className="ady-login-floor absolute inset-x-[6%] h-9 -translate-y-1/2"
            style={{ top: g.floorY }}
          />
          {BOARDS.map((b, j) => (
            <div
              key={b.key}
              ref={(el) => {
                boardEls.current[j] = el;
              }}
              className="invisible absolute left-0"
              style={{ bottom: g.boardBottom, width: g.bw, transform: `translateX(${g.W + 12}px)` }}
            >
              <Board k={g.k} board={b} />
            </div>
          ))}
          {load ? (
            <div className="absolute inset-0">
              <Suspense fallback={null}>
                <LazyLoginStageScene
                  drive={drive}
                  state={
                    !ready || !pose
                      ? "idle"
                      : reduced
                        ? ph?.kind === "intro"
                          ? "waving"
                          : "idle"
                        : pose.state
                  }
                  speed={ready && pose && !reduced ? pose.speed : 1}
                  rootPx={g.rootPx}
                  floorY={g.floorY}
                  reducedMotion={reduced}
                  onReady={() => setReady(true)}
                />
              </Suspense>
            </div>
          ) : null}
          {/* Handles above root: the green grip sits in front of root's fist, as if held. */}
          {BOARDS.map((b, j) => (
            <span
              key={b.key}
              ref={(el) => {
                handleEls.current[j] = el;
              }}
              className="ady-login-handle absolute top-0 left-0 w-4 rounded-full opacity-0 transition-opacity duration-200"
              style={{
                height: Math.round(g.rootPx * 0.18),
                top: Math.round(g.gripY - g.rootPx * 0.09),
                transform: `translateX(${g.W + 12}px)`,
              }}
            />
          ))}
          {bubble ? (
            // The outer box centres the bubble over root; the bubble itself pops and floats.
            <div
              key={bubble.kind}
              className="absolute w-64 -translate-x-1/2 -translate-y-full"
              style={{ left: bubble.to, top: Math.round(g.floorY - g.rootPx * 1.04) }}
            >
              <p className="ady-root-greet relative rounded-lg border border-card bg-surface px-3 py-2 text-center text-sm font-semibold shadow-card">
                {bubble.kind === "bye" ? t("aiMascot.showcase.bye") : t("aiMascot.showcase.hello")}
              </p>
            </div>
          ) : null}
          <div
            className="absolute inset-x-0 flex flex-col items-center gap-1.5"
            style={{ bottom: Math.round(g.H * 0.04) }}
          >
            <div className="flex items-center gap-1.5">
              {BOARDS.map((b, j) => (
                <span
                  key={b.key}
                  className={cx(
                    "h-1.5 rounded-full transition-all duration-300",
                    j === active ? "w-5 bg-primary" : "w-1.5 bg-primary/25",
                  )}
                />
              ))}
            </div>
            <span className="text-xs font-medium text-fg-secondary">
              {active >= 0
                ? t(`aiMascot.showcase.boards.${BOARDS[active]!.key}.title`)
                : t("aiMascot.showcase.meet")}
            </span>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function Board({ k, board }: { k: number; board: BoardDef }) {
  const base = `aiMascot.showcase.boards.${board.key}` as const;
  return (
    <div
      className="flex origin-bottom-left flex-col gap-2 rounded-panel border border-card bg-surface px-4 pt-3.5 pb-3 shadow-3"
      style={{ width: BOARD_W, transform: `scale(${k})` }}
    >
      <div className="flex items-center gap-2.5">
        <span className="ady-bg-brand-tint flex size-10 flex-none items-center justify-center rounded-lg">
          <Icon name={board.icon} gradient />
        </span>
        <span className="flex flex-col">
          <span className="text-2xs font-semibold tracking-wide text-link uppercase">
            {t(`${base}.eyebrow`)}
          </span>
          <span className="text-lg leading-tight font-bold text-fg">{t(`${base}.title`)}</span>
        </span>
      </div>
      <p className="text-sm text-fg-secondary">{t(`${base}.lead`)}</p>
      <ul className="flex flex-col gap-1">
        {(["p1", "p2", "p3", "p4"] as const).map((pt) => (
          <li key={pt} className="flex items-start gap-2 text-sm text-fg">
            <Icon name="check" className="mt-0.5 flex-none text-success-fg" />
            <span>{t(`${base}.${pt}`)}</span>
          </li>
        ))}
      </ul>
      {/* The board's features at a glance: icons (or the service's mark) with a one-word label. */}
      <ul className="mt-1 flex justify-between gap-1 border-t border-subtle pt-2">
        {board.tiles.map((tile) => (
          <li key={tile.key} className="flex min-w-0 flex-1 flex-col items-center gap-1">
            <span className="flex size-8 items-center justify-center rounded-lg border border-card bg-surface shadow-1">
              {tile.brand ? (
                <BrandIcon brand={tile.brand} size={16} />
              ) : (
                <Icon name={tile.icon ?? "dot"} gradient />
              )}
            </span>
            <span className="max-w-full truncate text-2xs tracking-tight text-fg-secondary">
              {i18n.tDynamic(`${base}.tiles.${tile.key}`, "aiMascot.showcase.meet")}
            </span>
          </li>
        ))}
      </ul>
    </div>
  );
}
